package com.solevo.dist.theme.styles

import com.solevo.dist.theme.textPrimaryColor
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontStyle
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/**
 * Created by Ilija Vucetic on 13.9.25..
 * Copyright (c) 2025 Aktiia. All rights reserved.
 */

fun Modifier.h1() = this
    .fontFamily("Inter", "sans-serif")
    .fontWeight(FontWeight.SemiBold)   // 600
    .fontSize(32.px)
    .lineHeight(24.px)
    .letterSpacing((-0.1).px)
    .color(textPrimaryColor)

fun Modifier.h2() = this
    .fontFamily("Inter", "sans-serif")
    .fontWeight(FontWeight.SemiBold)
    .fontStyle(FontStyle.Normal)
    .fontSize(20.px)
    .lineHeight(22.px)
    .letterSpacing((-0.1).px)
    .textAlign(TextAlign.Center)
    .color(textPrimaryColor)

fun Modifier.textUnselected(): Modifier = this.then(
    Modifier
        .fontFamily("Inter", "sans-serif")
        .fontWeight(400)
        .fontSize(14.px)
        .lineHeight(24.px)
        .color(textPrimaryColor)
)

fun Modifier.textSelected(): Modifier = this.then(
    Modifier
        .fontFamily("Inter", "sans-serif")
        .fontWeight(600)
        .fontSize(14.px)
        .lineHeight(24.px)
        .color(textPrimaryColor)
)

// ========== Table Modifiers ==========

/**
 * Modifier for table header cells.
 * Applies: 12px font, Medium weight, gray color, uppercase, letter spacing
 */
fun Modifier.tableHeaderCell(): Modifier = this
    .fontFamily("Inter", "sans-serif")
    .fontSize(12.px)
    .fontWeight(FontWeight.SemiBold)
    .color(rgb(140, 149, 159))
    .padding(leftRight = 6.px)
    .styleModifier {
        property("text-transform", "uppercase")
        property("letter-spacing", ".04em")
    }

/**
 * Modifier for table body cells (normal text).
 * Applies: 14px font, Normal weight, dark color, text overflow handling
 */
fun Modifier.tableBodyCell(): Modifier = this
    .fontFamily("Inter", "sans-serif")
    .fontSize(14.px)
    .fontWeight(FontWeight.Normal)
    .color(rgb(36, 41, 47))
    .overflow(Overflow.Hidden)
    .textOverflow(TextOverflow.Ellipsis)
    .whiteSpace(WhiteSpace.NoWrap)

/**
 * Modifier for table body cells with bold text.
 * Applies: 14px font, SemiBold weight, dark color, text overflow handling
 */
fun Modifier.tableBodyCellBold(): Modifier = this
    .fontFamily("Inter", "sans-serif")
    .fontSize(14.px)
    .fontWeight(FontWeight.SemiBold)
    .color(rgb(36, 41, 47))
    .overflow(Overflow.Hidden)
    .textOverflow(TextOverflow.Ellipsis)
    .whiteSpace(WhiteSpace.NoWrap)

/**
 * Modifier for table rows (body rows).
 * Applies: padding, border bottom, center alignment
 */
fun Modifier.tableRow(): Modifier = this
    .padding(leftRight = 16.px, topBottom = 16.px)
    .borderBottom(1.px, LineStyle.Solid, rgb(240, 242, 246))
    .styleModifier {
        property("align-items", "center")
    }

/**
 * Modifier for table header rows.
 * Applies: background color, padding, border bottom, sticky positioning
 */
fun Modifier.tableHeaderRow(): Modifier = this
    .backgroundColor(rgb(250, 251, 252))
    .padding(leftRight = 16.px, topBottom = 14.px)
    .borderBottom(1.px, LineStyle.Solid, rgb(240, 242, 246))
    .styleModifier {
        property("align-items", "center")
        property("position", "sticky")
        property("top", "0")
        property("z-index", "10")
    }

// ========== Text Modifiers ==========

/**
 * Modifier for secondary/supporting text.
 * Applies: 12px font, gray color
 */
fun Modifier.secondaryText(): Modifier = this
    .fontFamily("Inter", "sans-serif")
    .fontSize(12.px)
    .color(rgb(120, 130, 140))

/**
 * Modifier for section titles.
 * Applies: 28px font, Bold weight, dark color
 */
fun Modifier.sectionTitle(): Modifier = this
    .fontFamily("Inter", "sans-serif")
    .fontSize(28.px)
    .fontWeight(FontWeight.Bold)
    .color(rgb(36, 41, 47))

/**
 * Modifier for clickable cards with hover and click effects.
 * Applies: cursor pointer, hover background transition
 * Note: Hover background should be applied via CSS :hover selector or state management
 */
fun Modifier.clickableCard(): Modifier = this
    .cursor(Cursor.Pointer)
    .styleModifier {
        property("transition", "background-color 0.2s ease")
    }

